import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, ImagePlus, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError, imgSrc } from "@/lib/api";

const inputCls = "w-full rounded-xl border border-[#D8D7CE] bg-white px-4 py-3 text-[15px] outline-none focus:border-[#0B14A0] transition-colors";
const empty = { title: "", excerpt: "", content: "", category: "Nyhet", image_url: "", published: true };

const ImageField = ({ value, onChange }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      onChange(data?.url || "");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="admin-news-image-input" />
      {value ? (
        <div className="relative aspect-[16/9] rounded-xl overflow-hidden bg-[#070B2E]">
          <img src={imgSrc(value)} alt="" className="w-full h-full object-cover" data-testid="admin-news-image-preview" />
          <button type="button" onClick={() => onChange("")} className="absolute top-2 right-2 p-1.5 rounded-full bg-white" data-testid="admin-news-image-remove"><X className="w-4 h-4" /></button>
        </div>
      ) : (
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="w-full aspect-[16/6] rounded-xl border-2 border-dashed border-[#D8D7CE] grid place-items-center text-[#3A3F5C] hover:border-[#0B14A0] transition-colors" data-testid="admin-news-image-upload-button">
          <span className="flex items-center gap-2 font-semibold">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />} {busy ? "Laddar upp…" : "Ladda upp bild"}</span>
        </button>
      )}
    </div>
  );
};

export const NewsEditor = ({ open, onOpenChange, item, onSaved }) => {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [lastKey, setLastKey] = useState(null);
  const key = open ? item?.id || "new" : null;
  if (key !== lastKey) {
    setLastKey(key);
    if (open) setForm(item ? { ...empty, ...item, image_url: item.image_url || "" } : empty);
  }
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { title: form.title, excerpt: form.excerpt, content: form.content, category: form.category || "Nyhet", image_url: form.image_url || null, published: form.published };
    try {
      if (item) await api.put(`/admin/news/${item.id}`, body);
      else await api.post("/admin/news", body);
      toast.success(item ? "Nyheten uppdaterades" : "Nyheten publicerades");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-lenis-prevent data-testid="admin-news-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{item ? "Redigera nyhet" : "Ny nyhet"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4 mt-2">
          <input required minLength={2} placeholder="Rubrik" value={form.title} onChange={set("title")} className={inputCls} data-testid="admin-news-title-input" />
          <input placeholder="Kategori (t.ex. Systemnyheter, Utbildning, AI)" value={form.category} onChange={set("category")} className={inputCls} data-testid="admin-news-category-input" />
          <textarea rows={2} placeholder="Ingress (kort sammanfattning)" value={form.excerpt} onChange={set("excerpt")} className={inputCls} data-testid="admin-news-excerpt-input" />
          <textarea required rows={9} placeholder="Brödtext – använd tomma rader för nya stycken" value={form.content} onChange={set("content")} className={inputCls} data-testid="admin-news-content-input" />
          <ImageField value={form.image_url} onChange={(v) => setForm((f) => ({ ...f, image_url: v }))} />
          <label className="flex items-center gap-3 text-sm font-semibold">
            <Switch checked={form.published} onCheckedChange={(v) => setForm((f) => ({ ...f, published: v }))} data-testid="admin-news-published-switch" />
            {form.published ? "Publicerad" : "Utkast"}
          </label>
          <button type="submit" disabled={saving} className="w-full inline-flex justify-center items-center gap-2 rounded-full bg-[#0B14A0] text-white py-3.5 font-semibold hover:bg-[#070B2E] transition-colors disabled:opacity-60" data-testid="admin-news-submit-button">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} {item ? "Spara ändringar" : "Publicera"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
